import { useState, useEffect, useCallback, useRef } from 'react';
import { postsAPI, Post } from '../utils/api';

interface UsePostsParams {
  type?: string;
  searchQuery?: string;
  tag?: string;
  limit?: number;
}

interface PostsResponse {
  posts: Post[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const usePosts = (params: UsePostsParams = {}, enabled: boolean = true): {
  posts: Post[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  fetchPosts: (refresh?: boolean) => Promise<void>;
  loadMore: () => void;
  retry: () => void;
  likePost: (postId: number) => Promise<{ liked: boolean; likes: number }>;
  createPost: (postData: {
    type: string;
    title: string;
    content: string;
    tags?: string[];
    images?: string[];
    // 증례 게시 전 확인 — 서버에서 case_study일 때 강제한다
    case_consent?: boolean;
    case_deidentified?: boolean;
  }) => Promise<void>;
  updatePost: (id: number, data: { title?: string; content?: string; tags?: string[]; type?: string }) => Promise<void>;
  deletePost: (id: number) => Promise<void>;
} => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: params.limit || 10,
    total: 0,
    totalPages: 0,
  });
  // 진행 중 여부·요청 세대는 ref로 — state는 콜백 클로저에 묶여 중복 요청을 막지 못했다.
  // 탭·검색이 바뀌면 세대가 올라가 이전 탭의 늦은 응답이 새 목록에 섞이지 않는다.
  const loadingRef = useRef(false);
  const requestIdRef = useRef(0);

  const fetchPosts = useCallback(async (refresh = false) => {
    if (!refresh && loadingRef.current) return;

    const requestId = ++requestIdRef.current;
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    const currentPage = refresh ? 1 : pageRef.current;

    try {
      const response = await postsAPI.getPosts({
        type: params.type,
        q: params.searchQuery,
        tag: params.tag,
        page: currentPage,
        limit: pagination.limit,
      });
      if (requestId !== requestIdRef.current) return; // 더 새 요청이 있다
      const data: PostsResponse = response.data;

      setPosts(prevPosts => {
        if (refresh) return data.posts;
        // 페이지 사이에 새 글이 올라와 경계가 밀리면 같은 글이 다시 온다 — ID로 중복 제거
        const existingIds = new Set(prevPosts.map(p => p.id));
        return [...prevPosts, ...data.posts.filter(p => !existingIds.has(p.id))];
      });
      setPagination(data.pagination);
      setHasMore(data.posts.length > 0 && Number(data.pagination.page) < Number(data.pagination.totalPages));
      pageRef.current = currentPage + 1;
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      setError(err.response?.data?.error || '게시글을 불러오는데 실패했습니다.');
    } finally {
      if (requestId === requestIdRef.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [params.type, params.searchQuery, params.tag, pagination.limit]);

  useEffect(() => {
    if (enabled) {
      pageRef.current = 1;
      // 이전 탭의 글이 새 탭 이름 아래 잠깐 보이지 않게 비우고 새로 부른다
      setPosts([]);
      setHasMore(true);
      fetchPosts(true);
    } else {
      requestIdRef.current++; // 비활성화(로그인·로그아웃 전환) 시 진행 중 응답 무시
      loadingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.type, params.searchQuery, params.tag, enabled]);

  const loadMore = () => {
    if (!loadingRef.current && hasMore && !error) {
      fetchPosts();
    }
  };

  // 첫 페이지가 실패했으면 처음부터, 추가 로드가 실패했으면 그 페이지부터 다시
  const retry = () => {
    fetchPosts(posts.length === 0);
  };

  const likePost = async (postId: number) => {
    try {
      // 서버가 돌려준 실제 상태·개수로 맞춘다(화면에서 ±1 추정하면 서버와 반대로 갈 수 있었다)
      const res = await postsAPI.likePost(postId);
      const liked = Boolean(res.data?.liked);
      const likes = Number(res.data?.likes) || 0;
      setPosts(prevPosts =>
        prevPosts.map(post => (post.id === postId ? { ...post, likes, liked } : post))
      );
      return { liked, likes };
    } catch (err: any) {
      throw new Error(err.response?.data?.error || '좋아요 처리에 실패했습니다.');
    }
  };

  const createPost = async (postData: {
    type: string;
    title: string;
    content: string;
    tags?: string[];
    images?: string[];
    // 증례 게시 전 확인 — 서버에서 case_study일 때 강제한다
    case_consent?: boolean;
    case_deidentified?: boolean;
  }) => {
    try {
      await postsAPI.createPost(postData);
      // 게시글 목록 새로고침
      fetchPosts(true);
    } catch (err: any) {
      const data = err.response?.data;
      // 식별정보 차단(PHI_DETECTED)은 무엇을 지워야 하는지까지 알려준다.
      const details: string[] = Array.isArray(data?.findings)
        ? data.findings.map((f: any) => `${f.label}: ${f.hint}`)
        : [];
      const base = data?.error || '게시글 작성에 실패했습니다.';
      throw new Error(details.length ? `${base}\n\n${details.join('\n')}` : base);
    }
  };

  const updatePost = async (
    id: number,
    data: { title?: string; content?: string; tags?: string[]; type?: string }
  ) => {
    try {
      await postsAPI.updatePost(id, data);
      setPosts(prev => prev.map(p => {
        if (p.id !== id) return p;
        const updated: Post = {
          ...p,
          title: data.title ?? p.title,
          content: data.content ?? p.content,
          tags: data.tags ?? p.tags,
          type: (data.type as Post['type']) ?? p.type,
        };
        return updated;
      }));
    } catch (err: any) {
      throw new Error(err.response?.data?.error || '게시글 수정에 실패했습니다.');
    }
  };

  const deletePost = async (id: number) => {
    try {
      await postsAPI.deletePost(id);
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch (err: any) {
      throw new Error(err.response?.data?.error || '게시글 삭제에 실패했습니다.');
    }
  };

  return {
    posts,
    loading,
    error,
    hasMore,
    pagination,
    fetchPosts,
    loadMore,
    retry,
    likePost,
    createPost,
    updatePost,
    deletePost,
  };
};
